import { useCountByStatus } from "@/api/bibliography/bibliography";
import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { FieldConfig } from "@/config/forms/general";
import {
  Author,
  Bibliography,
  Photography,
  PhotographyInBibliography,
} from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import PhotographyInBibliographyDrawer from "./drawer/PhotographyInBibliographyDrawer";

interface ComplexLinkProps {
  id: string;
  photography: Photography | undefined;
  bibliography: Bibliography | undefined;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
}

const columnsForBibliography = [
  {
    accessorKey: "bibliography.bibliographieDescriptionNumeroBibliographie",
    header: "bibliographieDescriptionNumeroBibliographie",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<PhotographyInBibliography> }) => {
      return (
        <ImageCell
          value={""}
          attachments={row.original.attachments ?? []}
          domain={"bibliography"}
        />
      );
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAnnee",
    header: "bibliographieDescriptionAnnee",
    size: 50,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
];

const columnsForPhotography = [
  {
    accessorKey: "photography.photosDescriptionNumeroEnregistrement",
    header: "N°",
    enableResizing: true,
    size: 50,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<PhotographyInBibliography> }) => {
      return (
        <ImageCell
          value={""}
          attachments={row.original.attachments ?? []}
          domain={"photography"}
        />
      );
    },
  },
  {
    accessorKey: "photography.photosDescriptionTitre",
    header: "Titre",
    size: 200,
    cell: (props: any) => {
      return <HtmlCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "photography.photosDescriptionTechnique",
    header: "Technique",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "photography.photosDescriptionTheme",
    header: "Thème",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "photography.photosDescriptionDate",
    header: "Date",
    size: 50,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
];

/**
 * PhotographyInBibliographyLinks component
 * @returns {JSX.Element}
 */
const PhotographyInBibliographyLinks = ({
  id,
  photography,
  fields,
  type,
  bibliography,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "photographyInBibliography",
    type,
  );

  const { mutate: deletePhotographyInBibliography } = useDeleteComplexLink(
    id,
    "photographyInBibliography",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deletePhotographyInBibliography({
        id,
        relation: "photographyInBibliography",
      });
    },
    [deletePhotographyInBibliography],
  );

  const columns: ColumnDef<PhotographyInBibliography, any>[] = useMemo(() => {
    return [
      ...(type === "photography"
        ? columnsForBibliography.map((column) => ({
            ...column,
            header: t(column.header),
          }))
        : columnsForPhotography.map((column) => ({
            ...column,
            header: t(column.header),
          }))),
      {
        accessorKey: "photographiesDansBibliographieDescriptionReproduitPage",
        header: t("photographiesDansBibliographieDescriptionReproduitPage"),
        cell: (props: any) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "photographiesDansBibliographieDescriptionCitePage",
        header: t("photographiesDansBibliographieDescriptionCitePage"),
        cell: (props: any) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "photographiesDansBibliographieDescriptionPhotographe",
        header: t("photographiesDansBibliographieDescriptionPhotographe"),
        cell: (props: any) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey:
          "photographiesDansBibliographieDescriptionReproductionCouleur",
        header: t(
          "photographiesDansBibliographieDescriptionReproductionCouleur",
        ),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? "Oui" : "Non"} />;
        },
      },
      {
        accessorKey: "photographiesDansBibliographieDescriptionNonPagine",
        header: t("photographiesDansBibliographieDescriptionNonPagine"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? "Oui" : "Non"} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: (props: any) => {
          const data = props.row.original as PhotographyInBibliography;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <PhotographyInBibliographyDrawer
                key={props.row.original.id}
                fields={fields}
                queryId={id}
                type={type}
                data={data}
              />
              {type === "photography" && (
                <ShowElements
                  type="bibliography"
                  id={props.row.original.bibliography.id}
                  elements={[
                    {
                      label: t("bibliographieDescriptionNumeroBibliographie"),
                      value:
                        props.row.original.bibliography
                          .bibliographieDescriptionNumeroBibliographie,
                      type: "text",
                    },
                    {
                      label: t("bibliographieDescriptionResumeLong"),
                      value:
                        props.row.original.bibliography
                          .bibliographieDescriptionResumeLong,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: props.row.original.bibliography.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "bibliography" && (
                <ShowElements
                  type="photography"
                  id={props.row.original.photography.id}
                  elements={[
                    {
                      label: t("photosDescriptionNumeroEnregistrement"),
                      value:
                        props.row.original.photography
                          .photosDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("photosDescriptionLegendeRedigee"),
                      value:
                        props.row.original.photography
                          .photosDescriptionLegendeRedigee,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: props.row.original.photography.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "photography") {
      return (
        <PhotographyToBibliographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          photography={photography}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    } else {
      return (
        <BibliographyToPhotographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          bibliography={bibliography}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    }
  }, [
    type,
    id,
    relatedData,
    fields,
    photography,
    isAddMode,
    columns,
    searchContent,
    bibliography,
  ]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">
          {type === "photography"
            ? t("linkedBibliographies")
            : t("linkedPhotographies")}
        </h2>
        {!isAddMode && (
          <Button variant="outline" onClick={() => setIsAddMode(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      {datatableConf}
    </div>
  );
};

type PhotographyToBibliographyLinksProps =
  LinksTableProps<PhotographyInBibliography> & {
    photography: Photography | undefined;
  };

type BibliographyToPhotographyLinksProps =
  LinksTableProps<PhotographyInBibliography> & {
    bibliography: Bibliography | undefined;
  };

//Composant pour Photographies
const PhotographyToBibliographyLinks = ({
  id,
  relatedData,
  fields,
  photography,
  isAddMode,
  columnsLink,
  searchContent,
}: PhotographyToBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;
  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resultType: "bibliography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        size: 50,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Bibliography> }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        size: 100,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "relatedAuthor",
        header: t("bibliographieDescriptionAuteur"),
        size: 200,
        cell: (props: any) => {
          return (
            <TextCell
              value={props.row.original.relatedAuthor
                .map((author: Author) => author.artistesAuteursDescriptionNom)
                .join(", ")}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: (props: any) => {
          const data = props.row.original as Bibliography;
          if (!photography) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="bibliography"
                id={props.row.original.id}
                elements={[
                  {
                    label: t("bibliographieDescriptionNumeroBibliographie"),
                    value:
                      props.row.original
                        .bibliographieDescriptionNumeroBibliographie,
                    type: "text",
                  },
                  {
                    label: t("bibliographieDescriptionResumeLong"),
                    value:
                      props.row.original.bibliographieDescriptionResumeLong,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: props.row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <PhotographyInBibliographyDrawer
                key={props.row.original.id}
                fields={fields}
                queryId={photography.id}
                type="photography"
                data={{
                  id: "",
                  bibliography: data,
                  photography: photography,
                  photographiesDansBibliographieDescriptionReproduitPage: "",
                  photographiesDansBibliographieDescriptionCitePage: "",
                  photographiesDansBibliographieDescriptionPhotographe: "",
                  photographiesDansBibliographieDescriptionReproductionCouleur:
                    false,
                  photographiesDansBibliographieDescriptionNonPagine: false,
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [fields, photography, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsBibliographies}
          data={notRelatedBibliographies?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: totalSize,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsBibliographies,
      columnsLink,
      getPagination,
      isAddMode,
      notRelatedBibliographies?.data,
      relatedData,
      totalSize,
    ],
  );

  return datatableConf;
};

// Composant pour Bibliographies
const BibliographyToPhotographyLinks = ({
  id,
  relatedData,
  fields,
  bibliography,
  isAddMode,
  columnsLink,
  searchContent,
}: BibliographyToPhotographyLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedPhotographies } =
    useGetNotRelatedResource<Photography>({
      resultType: "photography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsDocuments: ColumnDef<Photography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "photosDescriptionNumeroEnregistrement",
        header: t("photosDescriptionNumeroEnregistrement"),
        enableResizing: true,
        size: 50,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Photography> }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"photography"}
            />
          );
        },
      },
      {
        accessorKey: "photosDescriptionTitre",
        header: t("photosDescriptionTitre"),
        size: 200,
        cell: (props) => {
          return <HtmlCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "photosDescriptionTechnique",
        header: t("photosDescriptionTechnique"),
        size: 100,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "photosDescriptionTheme",
        header: t("photosDescriptionTheme"),
        size: 100,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "photosDescriptionDate",
        header: t("photosDescriptionDate"),
        size: 100,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: (props: any) => {
          const data = props.row.original as Photography;
          if (!bibliography) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="photography"
                id={props.row.original.id}
                elements={[
                  {
                    label: t("photosDescriptionNumeroEnregistrement"),
                    value:
                      props.row.original.photosDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("photosDescriptionLegendeRedigee"),
                    value: props.row.original.photosDescriptionLegendeRedigee,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: props.row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <PhotographyInBibliographyDrawer
                key={props.row.original.id}
                fields={fields}
                queryId={bibliography.id}
                type="bibliography"
                data={{
                  id: "",
                  bibliography: bibliography,
                  photography: data,
                  photographiesDansBibliographieDescriptionReproduitPage: "",
                  photographiesDansBibliographieDescriptionCitePage: "",
                  photographiesDansBibliographieDescriptionPhotographe: "",
                  photographiesDansBibliographieDescriptionReproductionCouleur:
                    false,
                  photographiesDansBibliographieDescriptionNonPagine: false,
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [bibliography, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsDocuments}
          data={notRelatedPhotographies?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedPhotographies?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsDocuments,
      columnsLink,
      getPagination,
      isAddMode,
      notRelatedPhotographies?.data,
      notRelatedPhotographies?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};

export default PhotographyInBibliographyLinks;
